import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getPaymentsByFoodTruck, createPaymentCheckout, getFoodTruck, getMyCotisationStatus, getMyAchatsBureau, payAchatBureau, createAchatBureauCheckout } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Wallet,
  Receipt,
  CreditCard,
  CheckCircle,
  Clock,
  Euro,
  Calendar,
  Loader2,
  AlertCircle,
  TrendingUp,
  Download,
  ShoppingBag,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to download PDF with authentication
const downloadPdfWithAuth = async (url, filename) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Download error:', error);
    toast.error('Erreur lors du téléchargement de la facture');
  }
};

export default function MesPaiementsPage() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [foodTruck, setFoodTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [userCotisation, setUserCotisation] = useState(null);
  const [achatsBureau, setAchatsBureau] = useState([]);
  const [totalAchatsDu, setTotalAchatsDu] = useState(0);
  const [payingAchat, setPayingAchat] = useState(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      // Load user's cotisation status (one cotisation per user per year)
      const cotisStatusRes = await getMyCotisationStatus();
      setUserCotisation(cotisStatusRes.data);
      
      // Load achats bureau for this user
      try {
        const achatsBureauRes = await getMyAchatsBureau();
        setAchatsBureau(achatsBureauRes.data.achats || []);
        setTotalAchatsDu(achatsBureauRes.data.total_du || 0);
      } catch (e) {
        console.log("No achats bureau or not applicable");
      }
      
      // If user has a food truck, load food truck data for payments history
      if (user?.food_truck_id) {
        const [ftRes, paymentsRes] = await Promise.all([
          getFoodTruck(user.food_truck_id),
          getPaymentsByFoodTruck(user.food_truck_id)
        ]);
        
        setFoodTruck(ftRes.data);
        setPayments(paymentsRes.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handlePayAchatBureau = async () => {
    if (!payingAchat) return;
    try {
      await payAchatBureau(payingAchat.achat_id, user.food_truck_id);
      toast.success("Paiement enregistré");
      setPayingAchat(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du paiement");
    }
  };

  const handlePayAchatBureauOnline = async (achat) => {
    setPayingId(`achat-${achat.achat_id}`);
    try {
      const response = await createAchatBureauCheckout(achat.achat_id);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setPayingId(null);
    }
  };

  const handlePayOnline = async (cotisation) => {
    setPayingId(cotisation.id);
    try {
      const response = await createPaymentCheckout({
        package_id: "cotisation_annuelle",
        origin_url: window.location.origin,
        cotisation_id: cotisation.id,
        user_id: user?.id
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Erreur lors de la création du paiement");
      setPayingId(null);
    }
  };

  // Calculate totals from user cotisation only
  const userCotis = userCotisation?.cotisation;
  const cotisationDue = userCotis && !userCotis.payee && userCotis.statut !== "paye" 
    ? (userCotis.montant || 50) 
    : 0;
  const totalDu = cotisationDue + totalAchatsDu;
  const totalPaye = userCotis && (userCotis.payee || userCotis.statut === "paye")
    ? (userCotis.montant || 50)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mes-paiements-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-primary">
          Mes Paiements
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">
          Gérez vos cotisations et paiements
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className={`bg-white border-[#E5E0DC] ${totalDu > 0 ? "border-l-4 border-l-status-warning" : ""}`}>
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className={`p-2 sm:p-3 rounded-lg ${totalDu > 0 ? "bg-status-warning/10" : "bg-status-success/10"}`}>
              {totalDu > 0 ? (
                <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-status-warning" />
              ) : (
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-status-success" />
              )}
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-secondary">{totalDu.toFixed(2)} €</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Solde dû</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 bg-status-success/10 rounded-lg">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-status-success" />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-secondary">{totalPaye.toFixed(2)} €</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Total payé</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 bg-primary/10 rounded-lg">
              <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-secondary">{userCotisation?.cotisation ? 1 : 0}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Cotisations</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cotisation impayée */}
      {userCotis && !userCotis.payee && userCotis.statut !== "paye" && (
        <Card className="bg-white border-[#E5E0DC] border-l-4 border-l-status-warning">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="font-heading text-lg sm:text-xl text-secondary flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-status-warning" />
              Cotisation à payer
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="flex items-center justify-between p-4 bg-[#F9F7F5] rounded-lg">
              <div>
                <p className="font-semibold text-secondary">
                  Cotisation {userCotis.annee}
                </p>
                <p className="text-sm text-muted-foreground">
                  Échéance : {userCotis.date_echeance 
                    ? format(parseISO(userCotis.date_echeance), "dd MMMM yyyy", { locale: fr })
                    : "Non définie"}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-primary">
                  {userCotis.montant || 150} €
                </span>
                <Button
                  onClick={() => handlePayOnline(userCotis)}
                  disabled={payingId === userCotis.id}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid={`pay-cotisation-${userCotis.id}`}
                >
                  {payingId === userCotis.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique des cotisations */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            Ma cotisation annuelle
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            // Only show user cotisation (one per user per year)
            const cotisation = userCotisation?.cotisation;
            
            if (!cotisation) {
              return (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune cotisation enregistrée</p>
                </div>
              );
            }
            
            const isPaid = cotisation.payee || cotisation.statut === "paye";
            const downloadUrl = `${API_URL}/api/invoices/cotisation/user/${user?.id}/${cotisation.annee}`;
            
            return (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9F7F5]">
                    <TableHead>Année</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Date de paiement</TableHead>
                    <TableHead>Facture</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">{cotisation.annee}</TableCell>
                    <TableCell className="font-semibold">{cotisation.montant || 150} €</TableCell>
                    <TableCell>
                      <Badge className={isPaid ? "bg-status-success" : "bg-status-warning text-white"}>
                        {isPaid ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Payée</>
                        ) : (
                          <><Clock className="w-3 h-3 mr-1" /> En attente {cotisation.relances_envoyees ? `(${cotisation.relances_envoyees} relances)` : ''}</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {cotisation.date_paiement 
                        ? format(parseISO(cotisation.date_paiement), "dd/MM/yyyy")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {isPaid && cotisation.id && (
                        <button
                          onClick={() => downloadPdfWithAuth(
                            downloadUrl,
                            `facture_cotisation_${cotisation.annee}.pdf`
                          )}
                          className="text-primary hover:underline flex items-center gap-1 cursor-pointer"
                          data-testid="download-cotisation-invoice"
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Achats Bureau - Parts à payer */}
      {achatsBureau.length > 0 && (
        <Card className="bg-white border-[#E5E0DC]">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-primary" />
              Achats Bureau (quote-part)
              {totalAchatsDu > 0 && (
                <Badge className="bg-status-warning text-white ml-2">
                  {totalAchatsDu.toFixed(2)} € à payer
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9F7F5]">
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Ma part</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {achatsBureau.map((achat) => (
                  <TableRow key={achat.achat_id} data-testid={`achat-row-${achat.achat_id}`}>
                    <TableCell>
                      {achat.date ? format(parseISO(achat.date), "dd/MM/yyyy") : "-"}
                    </TableCell>
                    <TableCell className="font-medium">{achat.description}</TableCell>
                    <TableCell className="font-semibold">{achat.montant_du?.toFixed(2)} €</TableCell>
                    <TableCell>
                      {achat.statut_paiement === "paye" ? (
                        <Badge className="bg-status-success text-white">
                          <CheckCircle className="w-3 h-3 mr-1" /> Payé
                        </Badge>
                      ) : (
                        <Badge className="bg-status-warning text-white">
                          <Clock className="w-3 h-3 mr-1" /> En attente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {achat.statut_paiement !== "paye" && (
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            onClick={() => handlePayAchatBureauOnline(achat)}
                            disabled={payingId === `achat-${achat.achat_id}`}
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`pay-achat-stripe-${achat.achat_id}`}
                          >
                            {payingId === `achat-${achat.achat_id}` ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-1" />
                            )}
                            Payer en ligne
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPayingAchat(achat)}
                            className="text-status-success border-status-success hover:bg-status-success hover:text-white"
                            data-testid={`pay-achat-manual-${achat.achat_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Marquer payé
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Historique des paiements */}
      {payments.length > 0 && (
        <Card className="bg-white border-[#E5E0DC]">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Historique des paiements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9F7F5]">
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map(payment => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.date 
                          ? format(parseISO(payment.date), "dd/MM/yyyy")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {payment.type?.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {payment.description}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-status-success">
                        +{payment.montant} €
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Confirmation de paiement achat bureau */}
      <AlertDialog open={!!payingAchat} onOpenChange={() => setPayingAchat(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-status-success" />
              Confirmer le paiement
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Vous allez marquer ce paiement comme effectué :</p>
              {payingAchat && (
                <div className="bg-[#F9F7F5] p-3 rounded-lg mt-2">
                  <p className="text-sm text-muted-foreground">{payingAchat.description}</p>
                  <p className="text-lg font-bold text-primary">{payingAchat.montant_du?.toFixed(2)} €</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePayAchatBureau}
              className="bg-status-success hover:bg-status-success/90"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmer le paiement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
